// 고객 상세 여정 주변 — 머리 부제 · 다음 할 일 · 연결된 문서 · 변경공사 주석(판정은 기존 전이·주 버튼 규칙을 그대로 부른다).

import type { StatusTone } from "../common/tone";
import { formatDate } from "../../lib/format";
import { isAwaitingApproval, sortAwaiting } from "../change-order/awaiting";
import { canEditDraft as canEditChangeOrder } from "../change-order/transitions";
import { CHANGE_ORDER_STATUS_LABEL, type ChangeOrder, type ChangeOrderStatus } from "../change-order/types";
import { changeOrderPath } from "../change-order/view";
import { canEditDraft as canEditContract, canSign } from "../contract/transitions";
import type { ContractStatus } from "../contract/types";
import {
  JOURNEY_STEP_LABEL,
  JOURNEY_STEPS,
  type JourneyContext,
  type JourneyContract,
  type JourneyPick,
  type JourneyProject,
} from "./journey";
import type { CustomerStage } from "./types";
import type { Portfolio } from "../portfolio/types";
import { projectPrimaryActionOf } from "../project/primary-action";
import { canStart, RECORDABLE_PROJECT_STATUSES } from "../project/transitions";
import { dDayLabel, dDayOf } from "../project/view";
import { canDecide, canEditDraft as canEditQuote } from "../quote/transitions";

/* ── 머리 부제 ─────────────────────────────────────────────── */

export interface SubtitleInput {
  projectCount: number;
  assigneeName: string | null;
  created_at: string;
  sourceLabel: string | null;
}

// 「재시공 고객 · 현장 3곳 · 담당 김디자 · 첫 접수 2025-01 홈페이지」 — 현장이 둘 이상이면 재시공 고객.
export const customerSubtitle = ({ projectCount, assigneeName, created_at, sourceLabel }: SubtitleInput): string => {
  const month = formatDate(created_at, "").slice(0, 7);
  return [
    projectCount >= 2 ? "재시공 고객" : null,
    projectCount > 0 ? `현장 ${projectCount}곳` : null,
    assigneeName ? `담당 ${assigneeName}` : null,
    month ? ["첫 접수", month, sourceLabel].filter(Boolean).join(" ") : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

export interface HeadStage {
  label: string;
  tone: StatusTone;
  // 이전 이탈 이력이 있다는 보조 표시.
  note: string;
}

// 머리 단계 뱃지 — 저장 단계가 이탈인데 여정이 견적으로 다시 시작됐으면(보드 배치와 같은 판정) 여정 단계를 보인다, 아니면 null(저장 단계 그대로).
export const headStageOf = (
  stage: CustomerStage,
  place: { pick: Pick<JourneyPick<unknown>, "kind">; ctx: Pick<JourneyContext, "pos" | "lost"> }
): HeadStage | null => {
  if (stage !== "LOST" || place.pick.kind === "site" || place.ctx.lost) return null;
  return { label: JOURNEY_STEP_LABEL[JOURNEY_STEPS[place.ctx.pos]], tone: "progress", note: "재문의" };
};

/* ── 다음 할 일 ────────────────────────────────────────────── */

export type TodoAction = { kind: "link"; label: string; href: string } | { kind: "start-quote"; label: string };

export interface CustomerTodo {
  tag: string;
  text: string;
  action: TodoAction | null;
}

export type TodoChangeOrder = Pick<ChangeOrder, "id" | "contract_id" | "project_id" | "seq" | "title" | "status" | "sent_at">;

export interface TodoInput {
  ctx: Pick<JourneyContext, "series" | "live" | "rough" | "pos" | "lost">;
  project: JourneyProject | null;
  // 이 현장의 변경공사 — 승인 대기·작성 중인 것이 할 일 줄로 붙는다.
  changeOrders: readonly TodoChangeOrder[];
  // stageButtons().contact — 아직 첫 연락 전인가.
  contactPending: boolean;
  today: string;
  // 이 현장의 시공사례 — 초안이면 「이어 쓰기」, 공개했으면 「보기」, 없으면 「만들기」.
  portfolio?: TodoPortfolio | null;
}

export type TodoPortfolio = Pick<Portfolio, "id" | "published">;

// 공개한 사례의 버튼 — 고객 상세·보드가 같은 문구를 쓴다.
export const PORTFOLIO_PUBLISHED_LABEL = "사례 보기";

const quoteHref = (id: string): string => `/app/quotes/${id}`;
const contractHref = (id: string): string => `/app/contracts/${id}`;
const projectHref = (id: string): string => `/app/projects/${id}`;
const tagAt = (pos: number): string => JOURNEY_STEP_LABEL[JOURNEY_STEPS[pos]];

// 현장 단계 — 현장 상세 머리줄의 주 버튼(projectPrimaryActionOf)과 같은 판정.
const projectTodo = (
  project: JourneyProject,
  contractStatus: ContractStatus,
  today: string,
  portfolio: TodoPortfolio | null
): CustomerTodo | null => {
  const tag = JOURNEY_STEP_LABEL[project.status];
  const kind = projectPrimaryActionOf({
    canStart: canStart(project, contractStatus),
    canRecord: RECORDABLE_PROJECT_STATUSES.includes(project.status),
    closed: project.status === "CLOSED",
  });
  switch (kind) {
    case "start": {
      const days = dDayOf(project.planned_start_date, today);
      const text =
        days === null
          ? "착공일을 정하고 현장을 착공합니다"
          : days < 0
            ?`착공일이 ${-days}일 지났습니다 — 착공 처리합니다`
            : days === 0
              ? "오늘 착공일입니다 — 현장을 착공합니다"
              : `착공 ${dDayLabel(days)} — 착공일에 현장을 착공합니다`;
      return { tag, text, action: { kind: "link", label: "현장 착공", href: projectHref(project.id) } };
    }
    case "record":
      return { tag, text: "오늘 사진과 현장일지를 남깁니다", action: { kind: "link", label: "오늘 기록", href: `${projectHref(project.id)}/today` } };
    case "portfolio":
      if (!portfolio) return { tag, text: "현장일지와 사진으로 시공사례를 만듭니다", action: { kind: "link", label: "사례 만들기", href: projectHref(project.id) } };
      return portfolio.published
        ? { tag, text: "공개한 시공사례를 봅니다", action: { kind: "link", label: PORTFOLIO_PUBLISHED_LABEL, href: `/app/portfolio/${portfolio.id}` } }
        : { tag, text: "만들어 둔 시공사례 초안을 이어 씁니다", action: { kind: "link", label: "사례 이어 쓰기", href: `/app/portfolio/${portfolio.id}` } };
    case null:
      return null;
  }
};

// 현장 전 단계 — 견적·계약 전이표(canEditDraft·canDecide·canSign)로 지금 누를 것을 고른다.
const salesTodo = (ctx: TodoInput["ctx"], contactPending: boolean): CustomerTodo | null => {
  const { series, live, rough, pos } = ctx;
  const tag = tagAt(pos);
  if (pos === 4 && live) {
    return { tag, text: "체결한 계약으로 현장을 만듭니다", action: { kind: "link", label: "계약서 보기", href: contractHref(live.id) } };
  }
  if (pos === 3) {
    if (!live) {
      const won = series?.latest;
      return won ? { tag, text: "수주한 견적으로 계약서를 만듭니다", action: { kind: "link", label: "견적 보기", href: quoteHref(won.id) } } : null;
    }
    if (canEditContract(live.status)) {
      return { tag, text: "계약서 초안을 마무리해 고객에게 보냅니다", action: { kind: "link", label: "계약서 보기", href: contractHref(live.id) } };
    }
    if (canSign(live.status)) {
      return { tag, text: "고객 서명을 기다립니다", action: { kind: "link", label: "계약서 보기", href: contractHref(live.id) } };
    }
    return null;
  }
  if (pos === 2 && series) {
    const q = series.latest;
    if (canEditQuote(q.status)) return { tag, text: "상세견적 초안을 마무리해 보냅니다", action: { kind: "link", label: "견적 이어 쓰기", href: quoteHref(q.id) } };
    if (canDecide(q.status)) return { tag, text: "고객 답변을 받아 수주·이탈을 기록합니다", action: { kind: "link", label: "견적 보기", href: quoteHref(q.id) } };
    return null;
  }
  if (pos === 1 && rough) {
    if (canEditQuote(rough.status)) return { tag, text: "가견적 초안을 마무리해 보냅니다", action: { kind: "link", label: "견적 이어 쓰기", href: quoteHref(rough.id) } };
    return { tag, text: "가견적 답을 들었으면 상세견적을 만듭니다", action: { kind: "start-quote", label: "상세견적 작성" } };
  }
  if (pos === 0) {
    return {
      tag,
      text: contactPending ? "아직 연락 전입니다 — 상담하고 견적을 만듭니다" : "상담 내용으로 견적을 만듭니다",
      action: { kind: "start-quote", label: "견적 작성" },
    };
  }
  return null;
};

// 변경공사 줄 — 고객 승인 대기(오래된 것부터) 다음 작성 중.
const changeOrderTodos = (rows: readonly TodoChangeOrder[]): CustomerTodo[] => [
  ...sortAwaiting(rows.filter(isAwaitingApproval)).map((co) => ({
    tag: "변경공사",
    text: `${co.seq}차 ${co.title} — 고객 승인 대기`,
    action: { kind: "link" as const, label: "변경공사 보기", href: changeOrderPath(co.contract_id, co.id) },
  })),
  ...rows
    .filter((co) => canEditChangeOrder(co.status))
    .sort((a, b) => a.seq - b.seq)
    .map((co) => ({
      tag: "변경공사",
      text: `${co.seq}차 ${co.title} — 작성 중`,
      action: { kind: "link" as const, label: "이어 쓰기", href: changeOrderPath(co.contract_id, co.id) },
    })),
];

// 이탈한 여정은 할 일이 없다 — 현장이 있으면 현장 단계 한 줄 + 그 현장의 변경공사.
export const customerTodos = ({ ctx, project, changeOrders, contactPending, today, portfolio = null }: TodoInput): CustomerTodo[] => {
  if (ctx.lost) return [];
  if (!project) {
    const todo = salesTodo(ctx, contactPending);
    return todo ? [todo] : [];
  }
  const main = projectTodo(project, ctx.live?.status ?? "VOID", today, portfolio);
  return [...(main ? [main] : []), ...changeOrderTodos(changeOrders.filter((co) => co.project_id === project.id))];
};

/* ── 연결된 문서 ───────────────────────────────────────────── */

export type LinkedDoc =
  | { kind: "contract"; id: string; label: string; href: string; status: ContractStatus; date: string }
  | { kind: "change"; id: string; label: string; href: string; status: ChangeOrderStatus; date: string };

export type DocContract = Pick<JourneyContract, "id" | "contract_no" | "status" | "created_at" | "signed_at">;
export type DocChangeOrder = Pick<ChangeOrder, "id" | "contract_id" | "seq" | "title" | "status" | "created_at" | "sent_at" | "decided_at">;

// 계약(만든 순) 뒤에 그 계약의 변경공사(차수 순) — 견적·현장은 이미 자기 카드가 있어 뺀다.
export const linkedDocs = (
  contracts: readonly DocContract[],
  changeOrders: readonly DocChangeOrder[],
  siteTitle: (contractId: string) => string | null
): LinkedDoc[] =>
  [...contracts]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .flatMap((c): LinkedDoc[] => [
      {
        kind: "contract",
        id: c.id,
        label: [c.contract_no, siteTitle(c.id)].filter(Boolean).join(" "),
        href: contractHref(c.id),
        status: c.status,
        date: formatDate(c.signed_at ?? c.created_at, ""),
      },
      ...changeOrders
        .filter((co) => co.contract_id === c.id)
        .sort((a, b) => a.seq - b.seq)
        .map((co): LinkedDoc => ({
          kind: "change",
          id: co.id,
          label: `${c.contract_no} ${co.seq}차 · ${co.title}`,
          href: changeOrderPath(co.contract_id, co.id),
          status: co.status,
          date: formatDate(co.decided_at ?? co.sent_at ?? co.created_at, ""),
        })),
    ]);

/* ── 스테퍼 아래 변경공사 주석 ─────────────────────────────── */

export interface ChangeOrderNote {
  count: number;
  text: string;
  href: string;
}

// 선택 현장의 변경공사 한 줄 — 승인 대기가 있으면 가장 오래 기다린 것, 없으면 마지막 차수(파기는 뺀다).
export const changeOrderNote = (rows: readonly TodoChangeOrder[], projectId: string | null): ChangeOrderNote | null => {
  if (!projectId) return null;
  const live = rows.filter((co) => co.project_id === projectId && co.status !== "VOID");
  if (live.length === 0) return null;
  const pick = sortAwaiting(live.filter(isAwaitingApproval))[0] ?? [...live].sort((a, b) => a.seq - b.seq).pop();
  if (!pick) return null;
  return {
    count: live.length,
    text: `${pick.seq}차 ${pick.title} · ${CHANGE_ORDER_STATUS_LABEL[pick.status]}`,
    href: changeOrderPath(pick.contract_id, pick.id),
  };
};
